import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { ChevronRight, Home } from 'lucide-react'
import { useHomeStore } from '@/features/home/store'

const PAGE_LABELS: Record<string, string> = {
  '/home': 'Home',
  '/quote': 'Quote',
  '/roi': 'ROI Calculator',
  '/scope': 'Scope',
  '/dashboard': 'Dashboards',
  '/dashboard/presales': 'Pre-Sales',
  '/dashboard/presales/performance': 'Performance',
  '/dashboard/presales/operations': 'Operations',
  '/dashboard/presales/calendar': 'Calendar',
  '/dashboard/implementations': 'Implementations',
  '/target-accounts': 'Target Accounts',
  '/settings': 'Settings',
}

export function Breadcrumbs() {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const selectedDeal = useHomeStore((s) => s.selectedDeal)

  const pathname = location.pathname
  const sessionId = searchParams.get('session')
  const draftId = searchParams.get('draft')

  // Don't show breadcrumbs on the home page
  if (pathname === '/home' || pathname === '/') return null

  const crumbs: Array<{ label: string; to?: string }> = []

  // Home is always the first crumb
  crumbs.push({ label: 'Home', to: '/home' })

  // If there's a deal context, show it as second crumb
  if (selectedDeal && sessionId) {
    const dealLabel = selectedDeal.companyName || selectedDeal.name
    crumbs.push({ label: dealLabel, to: '/home' })
  } else if (draftId) {
    crumbs.push({ label: 'Draft' })
  }

  // Build the current page label
  // Handle nested dashboard routes
  if (pathname.startsWith('/dashboard/presales/deal/')) {
    crumbs.push({ label: 'Dashboards', to: '/dashboard' })
    crumbs.push({ label: 'Pre-Sales', to: '/dashboard/presales/performance' })
    crumbs.push({ label: 'Deal Detail' })
  } else if (pathname.startsWith('/dashboard/presales/')) {
    crumbs.push({ label: 'Dashboards', to: '/dashboard' })
    const pageLabel = PAGE_LABELS[pathname] || 'Pre-Sales'
    crumbs.push({ label: pageLabel })
  } else if (pathname.startsWith('/dashboard/')) {
    crumbs.push({ label: 'Dashboards', to: '/dashboard' })
    const pageLabel = PAGE_LABELS[pathname] || pathname.split('/').pop() || ''
    crumbs.push({ label: pageLabel })
  } else if (pathname.startsWith('/settings/')) {
    crumbs.push({ label: 'Settings', to: '/settings' })
    const tab = pathname.split('/').pop()
    if (tab) crumbs.push({ label: tab.charAt(0).toUpperCase() + tab.slice(1) })
  } else if (pathname === '/scope/schema') {
    crumbs.push({ label: 'Scope', to: '/scope' })
    crumbs.push({ label: 'Schema Viewer' })
  } else {
    const pageLabel = PAGE_LABELS[pathname]
    if (pageLabel && pageLabel !== 'Home') {
      crumbs.push({ label: pageLabel })
    }
  }

  // Don't render if only Home crumb
  if (crumbs.length <= 1) return null

  return (
    <nav className="flex items-center gap-1.5 text-xs text-[var(--text-muted)] mb-4" aria-label="Breadcrumb">
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1

        return (
          <span key={index} className="flex items-center gap-1.5">
            {index > 0 && <ChevronRight size={12} className="text-[var(--text-muted)]/50" />}
            {crumb.to && !isLast ? (
              <Link
                to={crumb.to}
                className="hover:text-[var(--text-primary)] transition-colors flex items-center gap-1"
              >
                {index === 0 && <Home size={12} />}
                {crumb.label}
              </Link>
            ) : (
              <span className={isLast ? 'text-[var(--text-secondary)] font-medium' : ''}>
                {index === 0 && <Home size={12} className="inline mr-1" />}
                {crumb.label}
              </span>
            )}
          </span>
        )
      })}
    </nav>
  )
}
